/**
 * Order Section
 * Order selection (with menu configuration check)
 */

import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { RadioGroup, RadioButton } from '@/components/base/radio-buttons/radio-buttons';
import type { ContainerFormData, SelectOption } from '@/hooks/useContainerForm';
import { useTranslation } from 'react-i18next';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';

interface OrderSectionProps {
    formData: ContainerFormData;
    onInputChange: (field: keyof ContainerFormData, value: string) => void;
    orderOptions: SelectOption[];
}

export function OrderSection({
    formData,
    onInputChange,
    orderOptions,
}: OrderSectionProps) {
    const { t } = useTranslation('pages:containers');
    const { isMenuItemEnabled } = useMenuConfiguration();
    const isOrdersEnabled = isMenuItemEnabled('orders');

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.order')}</h2>
            <div className="space-y-6">
                {isOrdersEnabled && (
                    <RadioGroup
                        value={formData.orderIdMode}
                        onChange={(value) => onInputChange('orderIdMode', value)}
                        size="md"
                    >
                        <RadioButton value="select" label={t('form.fields.orderIdSelectExisting')} />
                        <RadioButton value="manual" label={t('form.fields.orderIdEnterManually')} />
                    </RadioGroup>
                )}

                {!isOrdersEnabled || formData.orderIdMode === 'manual' ? (
                    <Input
                        label={t('form.fields.purchaseOrderNumber', 'Purchase Order Number')}
                        placeholder={t('form.fields.orderIdPlaceholder')}
                        value={formData.purchaseOrderNumber}
                        onChange={(value) => onInputChange('purchaseOrderNumber', value)}
                        size="md"
                    />
                ) : (
                    <Select
                        label={t('form.fields.orderId')}
                        placeholder={t('form.selectOrder', 'Select an order...')}
                        items={orderOptions}
                        selectedKey={formData.purchaseOrderNumber}
                        onSelectionChange={(key) => onInputChange('purchaseOrderNumber', key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                )}
            </div>
        </div>
    );
}
